import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowLeft, 
  Lightbulb, 
  Target, 
  MapPin, 
  GraduationCap, 
  DollarSign, 
  FileText, 
  FolderOpen,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Users,
  Building2
} from "lucide-react";

export default function UserGuide() {
  const navigate = useNavigate();

  const guidelines = [
    {
      category: "Writing Effective Job Titles",
      icon: <Target className="w-5 h-5 text-blue-600" />,
      color: "blue",
      tips: [
        {
          title: "Be Specific with Seniority Level",
          good: "Senior Software Engineer, Junior Data Analyst, Lead Product Manager",
          bad: "Engineer, Analyst, Manager",
          explanation: "Including seniority level helps our AI generate appropriate experience requirements and salary ranges."
        },
        {
          title: "Include Specialization When Relevant",
          good: "Full Stack Developer (React/Node), Oncology Nurse Practitioner, DevOps Engineer - AWS",
          bad: "Developer, Nurse, IT Person",
          explanation: "Specializations help generate more targeted skills and qualifications."
        },
        {
          title: "Use Industry-Standard Terminology",
          good: "Registered Nurse (RN), Certified Public Accountant (CPA), Project Manager (PMP)",
          bad: "Medical Helper, Money Person, Task Coordinator",
          explanation: "Standard titles improve salary data accuracy from job boards and BLS data."
        }
      ]
    },
    {
      category: "Optimizing Location Selection",
      icon: <MapPin className="w-5 h-5 text-emerald-600" />,
      color: "emerald",
      tips: [
        {
          title: "Use City + State Format",
          good: "San Francisco, CA | Austin, TX | Boston, MA",
          bad: "Bay Area, Texas, New England",
          explanation: "Specific locations enable accurate cost-of-living adjustments (200+ metro areas supported)."
        },
        {
          title: "Consider Remote Options",
          good: "Remote | Remote (US) | Hybrid - Denver, CO",
          bad: "Work from home, Flexible",
          explanation: "Use 'Remote' for location-independent roles. Our system applies national averages for remote positions."
        },
        {
          title: "Multiple Location Strategy",
          good: "Create separate requisitions for each major location",
          bad: "Listing multiple cities in one requisition",
          explanation: "Each location has different salary ranges and market conditions. Generate separate reqs for accurate data."
        }
      ]
    },
    {
      category: "Education Requirements Best Practices",
      icon: <GraduationCap className="w-5 h-5 text-purple-600" />,
      color: "purple",
      tips: [
        {
          title: "Use the Two-Track System",
          good: "Select 'General Academic' for business/tech roles, 'Healthcare/Clinical' for medical positions",
          bad: "Typing education manually",
          explanation: "Our structured education selector ensures consistent, professional formatting."
        },
        {
          title: "Separate Required vs Preferred",
          good: "Required: Bachelor's | Preferred: Master's in relevant field",
          bad: "Bachelor's or Master's required",
          explanation: "Clear separation helps candidates self-qualify and improves applicant quality."
        },
        {
          title: "Include 'Related Field' Option",
          good: "Bachelor's in Computer Science, Engineering, or Related Field",
          bad: "Must have CS degree only",
          explanation: "Adding 'Related Field' broadens your candidate pool without lowering standards."
        }
      ]
    },
    {
      category: "Maximizing Salary Intelligence",
      icon: <DollarSign className="w-5 h-5 text-amber-600" />,
      color: "amber",
      tips: [
        {
          title: "Use 'Generate with AI' for Full Analysis",
          good: "Click 'Generate with AI' button for complete market research",
          bad: "Using 'Preview' only (limited salary data)",
          explanation: "Full generation queries 5+ data sources including BLS, job boards, and applies experience/location adjustments."
        },
        {
          title: "Specify Years of Experience",
          good: "5-7 years, 10+ years, Entry-level (0-2 years)",
          bad: "Some experience, Experienced",
          explanation: "Experience ranges trigger our salary multiplier matrix for more accurate compensation."
        },
        {
          title: "Note Security Clearance Requirements",
          good: "Select TS/SCI, Secret, or other clearance levels when applicable",
          bad: "Mentioning clearance only in additional context",
          explanation: "Security clearance selection automatically applies 15-25% salary premium adjustments."
        }
      ]
    },
    {
      category: "Using Enhanced Research (Premium)",
      icon: <Sparkles className="w-5 h-5 text-yellow-600" />,
      color: "yellow",
      tips: [
        {
          title: "Enable Enhanced Research for Deep Market Analysis",
          good: "Check 'Enhanced Research' box before generating your requisition",
          bad: "Generating without Enhanced Research for competitive roles",
          explanation: "Enhanced Research performs comprehensive multi-source analysis including competitor benchmarking, market trends, and deeper salary intelligence from premium data sources."
        },
        {
          title: "Best Use Cases for Enhanced Research",
          good: "Hard-to-fill roles, executive positions, niche specialties, competitive markets",
          bad: "Standard entry-level positions where basic data suffices",
          explanation: "Enhanced Research is most valuable when you need detailed competitive intelligence to attract top talent or price specialized roles accurately."
        },
        {
          title: "What Enhanced Research Adds",
          good: "Competitor analysis, market positioning insights, detailed benefits comparison, talent availability data",
          bad: "Expecting only salary data (basic generation covers that)",
          explanation: "Beyond salary, Enhanced Research provides strategic insights: who's hiring for similar roles, what perks competitors offer, and current market demand indicators."
        }
      ]
    },
    {
      category: "Creating Compelling Descriptions",
      icon: <FileText className="w-5 h-5 text-rose-600" />,
      color: "rose",
      tips: [
        {
          title: "Leverage the Additional Context Field",
          good: "Include: team size, reporting structure, key projects, growth opportunities",
          bad: "Leaving it blank",
          explanation: "Context helps our AI generate more personalized, engaging job descriptions."
        },
        {
          title: "Select Your Company Culture",
          good: "Choose from: Innovative, Collaborative, Fast-paced, etc.",
          bad: "Generic culture description",
          explanation: "Culture selection adjusts the tone and language of the generated description."
        },
        {
          title: "Specify Industry Accurately",
          good: "Select from our industry list (Healthcare, Aerospace & Defense, FinTech, etc.)",
          bad: "Leaving industry blank",
          explanation: "Industry selection enables sector-specific terminology and compliance requirements."
        }
      ]
    },
    {
      category: "Using Your Requisition Library",
      icon: <FolderOpen className="w-5 h-5 text-indigo-600" />,
      color: "indigo",
      tips: [
        {
          title: "Save to Library for Future Use",
          good: "Save frequently-used requisitions as templates",
          bad: "Regenerating similar positions from scratch",
          explanation: "Library items can be quickly duplicated and modified, saving time on recurring roles."
        },
        {
          title: "Use Descriptive Requisition Names",
          good: "Senior SWE - Backend - NYC - Q1 2026",
          bad: "New Position, Job 1",
          explanation: "Clear naming makes it easy to find and reuse requisitions later."
        },
        {
          title: "Export in Multiple Formats",
          good: "Export as PDF for formal distribution, Word for editing",
          bad: "Only using one format",
          explanation: "PDF maintains formatting; Word allows ATS customization before posting."
        }
      ]
    }
  ];

  const proTips = [
    {
      icon: <TrendingUp className="w-5 h-5" />,
      title: "Benchmark Against Market",
      description: "Use our salary analysis to ensure your offers are competitive. Review the confidence scores and data sources."
    },
    {
      icon: <Users className="w-5 h-5" />,
      title: "Tailor for Your Audience",
      description: "Adjust tone settings based on your target candidates - technical roles may prefer detailed specs, creative roles may prefer engaging narratives."
    },
    {
      icon: <Building2 className="w-5 h-5" />,
      title: "Highlight Your Brand",
      description: "Use the Company Brand field to ensure your organization's identity shines through in every requisition."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2" data-testid="guide-back-btn">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-slate-900">User Guide</h1>
          <div className="w-20"></div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-white/20 rounded-full">
              <Lightbulb className="w-8 h-8" />
            </div>
          </div>
          <h2 className="text-3xl font-bold mb-3">Maximize Your SmartReqs Experience</h2>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto">
            Follow these best practices to generate more accurate, compelling job requisitions 
            with better salary intelligence and market insights.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Pro Tips Banner */}
        <Card className="mb-8 border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-amber-600" />
              <h3 className="font-bold text-amber-900">Pro Tips</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {proTips.map((tip, index) => (
                <div key={index} className="bg-white/60 rounded-lg p-4 border border-amber-200">
                  <div className="text-amber-600 mb-2">{tip.icon}</div>
                  <h4 className="font-semibold text-sm text-amber-900 mb-1">{tip.title}</h4>
                  <p className="text-xs text-amber-800">{tip.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Guidelines Sections */}
        <div className="space-y-8">
          {guidelines.map((section, sectionIndex) => (
            <Card key={sectionIndex} className="overflow-hidden">
              <div className={`bg-${section.color}-50 border-b border-${section.color}-100 px-6 py-4`}>
                <div className="flex items-center gap-3">
                  {section.icon}
                  <h3 className="text-lg font-bold text-slate-900">{section.category}</h3>
                </div>
              </div>
              <CardContent className="p-6 space-y-6">
                {section.tips.map((tip, tipIndex) => (
                  <div key={tipIndex} className="space-y-3">
                    <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-xs font-bold text-slate-600">
                        {tipIndex + 1}
                      </span>
                      {tip.title}
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-8">
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span className="text-xs font-semibold text-emerald-700 uppercase">Do This</span>
                        </div>
                        <p className="text-sm text-emerald-900">{tip.good}</p>
                      </div>
                      <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle className="w-4 h-4 text-rose-600" />
                          <span className="text-xs font-semibold text-rose-700 uppercase">Avoid</span>
                        </div>
                        <p className="text-sm text-rose-900">{tip.bad}</p>
                      </div>
                    </div>
                    
                    <p className="text-sm text-slate-600 pl-8 italic">
                      💡 {tip.explanation}
                    </p>
                    
                    {tipIndex < section.tips.length - 1 && (
                      <hr className="border-slate-100 mt-4" />
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA Section */}
        <Card className="mt-8 bg-gradient-to-r from-blue-600 to-indigo-700 text-white border-0">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold mb-3">Ready to Create Better Requisitions?</h3>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto">
              Put these tips into practice and see the difference in your job requisition quality.
            </p>
            <div className="flex justify-center gap-4">
              <Button 
                variant="secondary" 
                size="lg"
                onClick={() => navigate(-1)}
                data-testid="guide-return-btn"
              >
                Return
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="bg-transparent text-white border-white hover:bg-white/10"
                onClick={() => navigate("/faq")}
                data-testid="guide-faq-btn"
              >
                View FAQ
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Contact Section */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>
            Have more questions? Contact us at{" "}
            <a href="mailto:admin@smartreqs.ai" className="text-blue-600 hover:underline">
              admin@smartreqs.ai
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
